import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { buildLockupStackedCompactSvg } from "../admin/logoAssets";

const NAV = [
  { label: "HOME", to: "/" },
  { label: "THE METHOD", to: "/the-method" },
  { label: "THE WORK", to: "/the-work" },
  { label: "THE FINAL TRIBUTE", to: "/final-tribute" },
  { label: "THE ANTHROPOLOGIST", to: "/the-anthropologist" },
  { label: "COMMISSION", to: "/commission" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [samplesOpen, setSamplesOpen] = useState(false);
  const location = useLocation();
  const toggleRef = useRef(null);
  const firstLinkRef = useRef(null);
  const wasOpen = useRef(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    setOpen(false);
    setSamplesOpen(false);
  }, [location.pathname]);

  // Close the Samples panel on Escape.
  useEffect(() => {
    if (!samplesOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSamplesOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [samplesOpen]);

  // Focus management: focus first link on open, return focus to toggle on close.
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => firstLinkRef.current?.focus());
      wasOpen.current = true;
      return () => cancelAnimationFrame(id);
    }
    if (wasOpen.current) {
      toggleRef.current?.focus();
      wasOpen.current = false;
    }
  }, [open]);

  // Close on Escape and trap focus within the open menu (toggle + links).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const nav = document.getElementById("site-nav");
      if (!nav) return;
      const focusables = [toggleRef.current, ...nav.querySelectorAll("a[href]")].filter(Boolean);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const idx = focusables.indexOf(document.activeElement);
      if (e.shiftKey) {
        if (idx <= 0) {
          e.preventDefault();
          last.focus();
        }
      } else if (idx === -1 || idx === focusables.length - 1) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <AnimatePresence>
        {(open || samplesOpen) && (
          <motion.div
            className="fixed inset-0 z-40"
            style={{
              backgroundColor: "rgba(28,26,23,0.30)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
            onClick={() => {
              setOpen(false);
              setSamplesOpen(false);
            }}
            aria-hidden="true"
            data-testid="menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.35, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>
      <header className="relative z-50 w-full bg-aged" data-testid="site-header">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between py-6 md:py-8">
          {/* Logo + wordmark */}
          <Link to="/" className="group min-w-0 block w-[168px] md:w-[188px]" data-testid="header-logo-link">
            <div dangerouslySetInnerHTML={{ __html: buildLockupStackedCompactSvg("ink") }} />
          </Link>

          {/* Menu toggle + Samples dropdown + persistent Commission */}
          <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-2 md:gap-x-8 shrink-0 self-end md:self-auto">
            {/* Menu toggle (all breakpoints) */}
            <button
              ref={toggleRef}
              className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] md:tracking-[0.28em] text-[#1C1A17] hover:text-[#7A2E2E] transition-colors"
              onClick={() => {
                setSamplesOpen(false);
                setOpen((v) => !v);
              }}
              data-testid="menu-toggle"
              aria-expanded={open}
              aria-controls="site-nav"
              aria-label="Toggle navigation menu"
            >
              {open ? "Close" : "Menu"}
            </button>
            <button
              type="button"
              data-testid="header-samples-toggle"
              onClick={() => {
                setOpen(false);
                setSamplesOpen((v) => !v);
              }}
              aria-expanded={samplesOpen}
              aria-controls="samples-nav"
              className={`text-[10px] md:text-[11px] uppercase tracking-[0.16em] md:tracking-[0.28em] transition-colors hover:text-[#7A2E2E] ${
                samplesOpen ? "text-[#7A2E2E]" : "text-[#1C1A17]"
              }`}
            >
              {samplesOpen ? "Close" : "Samples"}
            </button>
            <NavLink
              to="/commission"
              data-testid="header-commission-link"
              className={({ isActive }) =>
                `text-[10px] md:text-[11px] uppercase tracking-[0.16em] md:tracking-[0.28em] transition-colors hover:text-[#7A2E2E] ${
                  isActive ? "text-[#7A2E2E]" : "text-[#1C1A17]"
                }`
              }
            >
              Commission
            </NavLink>
          </div>
        </div>

        {/* Nav panel */}
        {open && (
          <nav id="site-nav" className="pb-8 flex flex-col gap-5" data-testid="site-nav">
            {NAV.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                ref={i === 0 ? firstLinkRef : undefined}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.28em] transition-colors duration-300 hover:text-[#7A2E2E] ${
                    isActive ? "text-[#7A2E2E]" : "text-[#1C1A17]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}

        {/* Samples panel */}
        {samplesOpen && (
          <nav id="samples-nav" className="pb-8 flex flex-col gap-5" data-testid="header-samples-menu">
            <NavLink
              to="/from-the-page"
              onClick={() => setSamplesOpen(false)}
              data-testid="header-life-study-sample"
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.28em] transition-colors duration-300 hover:text-[#7A2E2E] ${
                  isActive ? "text-[#7A2E2E]" : "text-[#1C1A17]"
                }`
              }
            >
              LIFE STUDY SAMPLE
            </NavLink>
            <NavLink
              to="/from-the-booklet"
              onClick={() => setSamplesOpen(false)}
              data-testid="header-final-tribute-sample"
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.28em] transition-colors duration-300 hover:text-[#7A2E2E] ${
                  isActive ? "text-[#7A2E2E]" : "text-[#1C1A17]"
                }`
              }
            >
              FINAL TRIBUTE SAMPLE
            </NavLink>
          </nav>
        )}
      </div>
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <hr className="border-0" style={{ height: "1px", backgroundColor: "#A8A296", opacity: 0.5 }} />
      </div>
    </header>
    </>
  );
};
